/**
 * Unified Workflow Designer — Shared types.
 *
 * Extracted from the monolithic UnifiedWorkflowDesigner.tsx.
 */

import type React from 'react';
import type { PrimitiveWorkflowType } from '../shared/ReactFlowCanvas';
import type { WorkflowMetadata } from '../shared/WorkflowManagementSidebar';
import type { PrimitiveInstance } from '../shared/PrimitivesLibrarySidebar';
import type { TemplateListItem } from '../metadata/services/MetadataTemplateService';
import type { MetadataInterpreter } from '../metadata/MetadataInterpreter';

// ── Ecosystem data types ──────────────────────────────────────────────

export interface WorkflowOnCanvas {
  id: string;
  type: string;
  name: string;
  x: number;
  y: number;
  status: string;
  health: number;
  connections: string[];
  metadata?: Record<string, unknown>;
}

export interface EcosystemTimelineItem {
  at_ms: number;
  transition: string;
  actor: string;
  source_wall: string;
  target_wall: string;
}

export interface EcosystemOptimization {
  id: string;
  recommendation_type: 'performance' | 'cost' | 'reliability' | string;
  description: string;
  impact: string;
  savings: string;
}

export interface CrossWallRelationship {
  primary_workflow: { name: string };
  dependent_workflows: Array<{ name: string }>;
}

export interface EcosystemData {
  workflows_on_canvas: WorkflowOnCanvas[];
  ecosystem_timeline: EcosystemTimelineItem[];
  ecosystem_optimizations: EcosystemOptimization[];
  cross_wall_relationships?: CrossWallRelationship[];
}

// ── Props ─────────────────────────────────────────────────────────────

/** Props passed to a custom workflow browser renderer. */
export interface WorkflowBrowserRenderProps {
  opened: boolean;
  onClose: () => void;
  onSelectWorkflow: (workflow: any) => void;
}

export interface UnifiedWorkflowDesignerProps {
  tenant: string;
  executiveMode?: boolean;
  onWorkflowCreate?: (type: string, position: { x: number; y: number }) => void;
  onOptimizationApply?: (recommendation: EcosystemOptimization) => void;
  /**
   * Custom workflow browser renderer. When provided, replaces the built-in
   * WorkflowBrowser with a portal-level component (e.g., TabulatorPrimitiveManager
   * in a Drawer). This allows the designer package to stay decoupled from
   * portal-specific components.
   */
  renderWorkflowBrowser?: (props: WorkflowBrowserRenderProps) => React.ReactNode;
  /**
   * Pre-populate the designer with a specific workflow on mount.
   * Both id and name are required — the backend query uses name to fetch the graph.
   */
  initialWorkflowId?: string;
  initialWorkflowName?: string;
}

// ── Designer state (exposed by useDesignerState) ──────────────────────

export interface DesignerState {
  // Workflow management
  savedWorkflows: WorkflowMetadata[];
  setSavedWorkflows: React.Dispatch<React.SetStateAction<WorkflowMetadata[]>>;
  currentWorkflow: WorkflowMetadata | null;
  setCurrentWorkflow: React.Dispatch<React.SetStateAction<WorkflowMetadata | null>>;
  workflowSidebarOpen: boolean;
  setWorkflowSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Canvas state
  ecosystemData: EcosystemData;
  setEcosystemData: React.Dispatch<React.SetStateAction<EcosystemData>>;
  selectedWorkflowId: string | null;
  setSelectedWorkflowId: React.Dispatch<React.SetStateAction<string | null>>;
  activeQuadrant: string;
  setActiveQuadrant: React.Dispatch<React.SetStateAction<string>>;
  designerMode: 'definition' | 'instance';
  setDesignerMode: React.Dispatch<React.SetStateAction<'definition' | 'instance'>>;
  commandCenterOpen: boolean;
  setCommandCenterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  joinerOpen: boolean;
  setJoinerOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Business templates
  showTemplates: boolean;
  setShowTemplates: React.Dispatch<React.SetStateAction<boolean>>;
  businessTemplates: TemplateListItem[];
  setBusinessTemplates: React.Dispatch<React.SetStateAction<TemplateListItem[]>>;
  selectedTemplate: TemplateListItem | null;
  setSelectedTemplate: React.Dispatch<React.SetStateAction<TemplateListItem | null>>;
  templateFormOpen: boolean;
  setTemplateFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activeTemplateInterpreter: MetadataInterpreter | null;
  setActiveTemplateInterpreter: React.Dispatch<React.SetStateAction<MetadataInterpreter | null>>;

  // Primitives library
  primitiveLibrary: PrimitiveInstance[];
  setPrimitiveLibrary: React.Dispatch<React.SetStateAction<PrimitiveInstance[]>>;
  selectedPrimitiveType: PrimitiveWorkflowType;
  setSelectedPrimitiveType: React.Dispatch<React.SetStateAction<PrimitiveWorkflowType>>;
  libraryCollapsed: boolean;
  setLibraryCollapsed: React.Dispatch<React.SetStateAction<boolean>>;

  // CRUD form
  formDialogOpen: boolean;
  setFormDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  formPrimitiveType: PrimitiveWorkflowType | null;
  setFormPrimitiveType: React.Dispatch<React.SetStateAction<PrimitiveWorkflowType | null>>;
  formMode: 'create' | 'edit' | 'view';
  setFormMode: React.Dispatch<React.SetStateAction<'create' | 'edit' | 'view'>>;
  formInitialData: Record<string, any> | undefined;
  setFormInitialData: React.Dispatch<React.SetStateAction<Record<string, any> | undefined>>;
  pendingDropPosition: { x: number; y: number } | null;
  setPendingDropPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;

  // Role browser (Issue #846: backend RBAC integration)
  roleBrowserOpen: boolean;
  setRoleBrowserOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Draft status
  lastDraftSave: Date | null;
  setLastDraftSave: React.Dispatch<React.SetStateAction<Date | null>>;
  draftSaving: boolean;
  setDraftSaving: React.Dispatch<React.SetStateAction<boolean>>;
  draftKey: string;
}

// ── Constants ─────────────────────────────────────────────────────────

export const DRAG_TRANSFER_KEY = 'application/ferp-workflow';

export function createEmptyEcosystemData(): EcosystemData {
  return {
    workflows_on_canvas: [],
    ecosystem_timeline: [],
    ecosystem_optimizations: [],
    cross_wall_relationships: [],
  };
}

/**
 * Quadrant-based workflow type organization (legacy 4-wall model).
 * Will be replaced by 5-phase strip in Phase 4.
 */
export const WORKFLOW_QUADRANTS = {
  DESIGN: {
    types: ['WorkflowDefinition', 'ActorDescriptor', 'TransitionCatalog', 'Policy'],
    position: 'top-left',
    color: '#e3f2fd',
    borderColor: '#2196f3',
    label: 'Design',
    description: 'Define & Structure',
  },
  CONFIGURE: {
    types: ['Context', 'Schedule', 'Resource', 'Integration'],
    position: 'top-right',
    color: '#f3e5f5',
    borderColor: '#9c27b0',
    label: 'Configure',
    description: 'Setup & Schedule',
  },
  EXECUTE: {
    types: ['Instance', 'Signal', 'Task', 'JobStatus'],
    position: 'bottom-left',
    color: '#e8f5e8',
    borderColor: '#4caf50',
    label: 'Execute',
    description: 'Run & Monitor',
  },
  ANALYZE: {
    types: ['ExecutionResult', 'TimelineItem', 'Escalation', 'OptimizationRecommendation'],
    position: 'bottom-right',
    color: '#fff3e0',
    borderColor: '#ff9800',
    label: 'Analyze',
    description: 'Review & Optimize',
  },
} as const;

export type QuadrantKey = keyof typeof WORKFLOW_QUADRANTS;
